<template>
    <el-card class="file-card" shadow="hover" :body-style="{ padding: '0' }">
        <!-- 缩略图区域 -->
        <div class="file-card__thumb" :class="'file-card__thumb--' + thumbType">
            <el-image v-if="fileType === 'image'" class="file-card__image" :src="imageUrl" fit="cover"
                :preview-src-list="[imageUrl]" preview-teleported lazy>
                <template #error>
                    <div class="file-card__slot">
                        <file-type-icon :name="item.uname" :size="48" />
                    </div>
                </template>
            </el-image>
            <div v-else-if="fileType === 'video'" class="file-card__slot">
                <el-icon>
                    <VideoPlay />
                </el-icon>
            </div>
            <div v-else class="file-card__slot">
                <file-type-icon :name="item.uname" />
            </div>
            <span v-if="fileType" class="file-card__badge">{{ typeLabel }}</span>
        </div>

        <!-- 文件信息区域 -->
        <div class="file-card__body">
            <el-tooltip :content="item.fileName" placement="top" :disabled="!item.fileName">
                <div class="file-card__name">{{ item.fileName }}</div>
            </el-tooltip>
            <ul class="file-card__meta">
                <li>
                    <el-icon>
                        <Refresh />
                    </el-icon>
                    <span :title="item.createTime">{{ item.createTime }}</span>
                </li>
                <li>
                    <el-icon>
                        <Calendar />
                    </el-icon>
                    <span :title="item.expiryTime">{{ item.expiryTime || '长期有效' }}</span>
                </li>
                <li>
                    <el-icon>
                        <Files />
                    </el-icon>
                    <span>大小：{{ sizeText }}</span>
                </li>
                <li>
                    <el-icon>
                        <Download />
                    </el-icon>
                    <span>下载：{{ item.downloadCount || 0 }}</span>
                </li>
                <li>
                    <el-icon>
                        <Clock />
                    </el-icon>
                    <span>历史：{{ item.historyCount || 0 }}</span>
                </li>
            </ul>
        </div>

        <!-- 操作区域 -->
        <div class="file-card__actions">
            <el-tooltip content="历史版本" placement="top">
                <el-button class="file-card__action" link @click="fire('history', item)">
                    <el-icon>
                        <Clock />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <el-tooltip content="下载" placement="top">
                <el-button class="file-card__action" link @click="fire('download', item)">
                    <el-icon>
                        <Download />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <el-tooltip content="复制下载链接" placement="top">
                <el-button class="file-card__action" link @click="fire('copy-url', item)">
                    <el-icon>
                        <Link />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <el-tooltip v-if="showShare" content="分享" placement="top">
                <el-button class="file-card__action" link @click="fire('share', item)">
                    <el-icon>
                        <Share />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <el-popover v-if="showDelete" placement="top" :width="200" trigger="click">
                <p>删除操作不可撤回，确定删除吗？</p>
                <div style="text-align: right; margin: 0">
                    <el-button type="danger" size="small" @click="fire('delete', item)">确定删除</el-button>
                </div>
                <template #reference>
                    <el-button class="file-card__action file-card__action--danger" link>
                        <el-icon>
                            <Delete />
                        </el-icon>
                    </el-button>
                </template>
            </el-popover>
        </div>
    </el-card>
</template>

<script>
import { config } from '@/utils/config';
import FileTypeIcon from './FileTypeIcon';
export default {
    name: 'FileManagerCard',
    components: { FileTypeIcon },
    props: {
        // 文件信息
        item: {
            type: Object,
            required: true,
            default: () => ({})
        },
        // 是否显示分享按钮
        showShare: {
            type: Boolean,
            default: false
        },
        // 是否显示删除按钮
        showDelete: {
            type: Boolean,
            default: false
        }
    },
    emits: ['history', 'download', 'copy-url', 'share', 'delete'],
    computed: {
        fileType() {
            return this.getFileType(this.item.uname);
        },
        // 仅视频使用彩色底，其余类型统一浅色底 + 类型图标
        thumbType() {
            return this.fileType === 'video' ? 'video' : 'plain';
        },
        typeLabel() {
            let labelMap = {
                image: '图片',
                video: '视频'
            };
            return labelMap[this.fileType] || '文件';
        },
        imageUrl() {
            return config().imageServer + this.item.uname;
        },
        // 文件大小，按 B / KB / MB 自适应显示
        sizeText() {
            let size = Number(this.item.size) || 0;
            if (size >= 1024 * 1024) {
                return (size / 1024 / 1024).toFixed(1) + ' MB';
            }
            if (size >= 1024) {
                return (size / 1024).toFixed(1) + ' KB';
            }
            return size + ' B';
        }
    },
    methods: {
        getFileType(name) {
            if (!name) {
                return '';
            }
            let extName = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
            switch (extName) {
                case 'png':
                case 'jpg':
                case 'jpeg':
                case 'gif':
                case 'bmp':
                case 'webp':
                case 'svg':
                case 'ico':
                    return 'image';
                case 'mp4':
                case 'mov':
                case 'webm':
                case 'mkv':
                case 'avi':
                case 'flv':
                case 'wmv':
                case 'm4v':
                    return 'video';
            }
            return '';
        },
        fire(event, item) {
            this.$emit(event, item);
        }
    }
};
</script>

<style scoped>
.file-card {
    margin: 10px;
    border-radius: 10px;
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
}

.file-card:hover {
    transform: translateY(-4px);
}

/* 缩略图 */
.file-card__thumb {
    position: relative;
    width: 100%;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.file-card__thumb--plain {
    background-color: #f2f4f8;
}

.file-card__thumb--video {
    background: linear-gradient(135deg, #409eff, #1e6fd9);
}

.file-card__image {
    width: 100%;
    height: 100%;
}

.file-card__slot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.file-card__thumb--video .file-card__slot {
    font-size: 48px;
    color: rgba(255, 255, 255, 0.9);
}

.file-card__badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 18px;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.45);
}

/* 文件信息 */
.file-card__body {
    padding: 10px 12px 0;
}

.file-card__name {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.file-card__meta {
    margin: 8px 0 10px;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 8px;
    font-size: 12px;
    color: #909399;
}

.file-card__meta li {
    display: flex;
    align-items: center;
    min-width: 0;
}

.file-card__meta li .el-icon {
    flex: none;
    margin-right: 4px;
    font-size: 13px;
}

.file-card__meta li span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* 操作栏 */
.file-card__actions {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 4px 6px;
    border-top: 1px solid #ebeef5;
}

.file-card__actions :deep(.el-button + .el-button) {
    margin-left: 0;
}

.file-card__action {
    color: #606266;
    font-size: 16px;
    padding: 4px 8px;
    height: auto;
}

.file-card__action:hover {
    color: #409eff;
}

.file-card__action--danger:hover {
    color: #f56c6c;
}
</style>
